import { ValidationService, FactorConfigurationService, LanguageService, TelemetryService, NotificationService, ActionType, BrowserNavigationService, ConfirmModalComponent, errorShouldBeSentToAI, NgbOffcanvas } from '@aston/foundation';
import { map, switchMap, withLatestFrom, first, catchError, filter, tap, debounceTime, mergeMap, delay, auditTime, distinctUntilChanged } from 'rxjs/operators';
import { Injectable, Injector, inject, runInInjectionContext } from '@angular/core';
import { environment } from 'apps/middle/src/environments/environment';
import { Observable, of, EMPTY, from, combineLatest } from 'rxjs';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { FeatureFlagsService } from '@aston/feature-flags';
import { UserPrefsStoreActions } from '@aston/user-prefs';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ThemeManagerService } from '@aston/themes';
import { Action, Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { saveAs } from 'file-saver';

import { DocumentFileService, WebSocketService, TenantService, InactivityChannelService, DashboardRoutesService } from '../../shared-module/services';
import { DocumentFilesViewerComponent } from '../../shared-module/components/document-files-viewer/document-files-viewer.component';
import { InactivityModalComponent } from '../../authentication-module/components/inactivity-modal/inactivity-modal.component';
import { ImportDetailStore } from '../../import-module/stores/import-detail/import-detail.store';
import * as DunningActionDetailStoreActions from '../dunning-action-detail-store/actions';
import { ScriptLoaderService } from '../../shared-module/services/script-loader.service';
import { ExtraDataStore } from '../../shared-module/stores/extradata/extradata.store';
import { AuthenticationService } from '../../authentication-module/services';
import { TenantUrl } from '../../shared-module/functions/tenant-url.class';
import { ReferentialStore } from '../../shared-module/stores/referential';
import { IUserSession } from '../../authentication-module/models';
import { IConfirmModalResult } from '../../shared-module/models';
import * as RoutesDefinitions from '../../routes-definitions';
import { DefaultFeatureFlags } from '../../app.feature-flags';
import { AppConfiguration } from '../../app.configuration';
import { UserService } from '../../user-module/services';
import { WsEventType } from '../../shared-module/enums';
import { AppConstants } from '../../app.constants';

import * as featureSelectors from './selectors';
import * as featureActions from './actions';

@Injectable({
	providedIn: 'root'
})
export class AppStoreEffects {

	private userService = inject(UserService);
	private router = inject(Router);
	private actions$ = inject(Actions);
	private store = inject(Store);
	private appConfig = inject(AppConfiguration);
	private validationService = inject(ValidationService);
	private factorConfigurationService = inject(FactorConfigurationService);
	private tenantService = inject(TenantService);
	private modalService = inject(NgbModal);
	private offcanvasService = inject(NgbOffcanvas);
	private inactivityService = inject(InactivityChannelService);
	private notificationService = inject(NotificationService);
	private languageService = inject(LanguageService);
	private translateService = inject(TranslateService);
	private scriptLoaderService = inject(ScriptLoaderService);
	private documentFileService = inject(DocumentFileService);
	private authenticationService = inject(AuthenticationService);
	private browserNav = inject(BrowserNavigationService);
	private themeService = inject(ThemeManagerService);
	private dashboardRoutes = inject(DashboardRoutesService);
	private referential = inject(ReferentialStore);
	private extraStore = inject(ExtraDataStore);
	private importDetailStore = inject(ImportDetailStore);
	private ffs = inject(FeatureFlagsService);
	private injector = inject(Injector);
	private ws = inject(WebSocketService);

	isInactivityWarningModalActive = false;

	/**
	 * General action logging to AppInsight
	 */
	catchallLogEffect$ = createEffect(() => this.actions$.pipe(
		ofType(
			featureActions.Logout,
			featureActions.ToastError,
			featureActions.NavigateToGate,
			featureActions.OpenUrl,
			featureActions.PrintUrl,
			featureActions.OpenDocument,
			featureActions.PrintDocument,
			featureActions.DownloadDocument,
			featureActions.NavigateToConsole,
			featureActions.NavigateToHelpPage,

			// WorkflowSchedulingStoreActions.StartConfigRequest,

			// SellersStoreActions.ExportSellersRequest,
			// TransfersStoreActions.ExportTransfersRequest,
			// SellerPageStoreActions.ExportSellerCcvRequest,
			// AccountingStoreActions.ExportAccountingRequest,
			// SuperDebtorsStoreActions.ExportSuperDebtorsRequest,
			// DunningCasesStoreActions.ExportDunningCasesRequest,
			// DunningActionsStoreActions.ExportDunningActionsRequest,
			// SuperDebtorPageStoreActions.ExportAccountingRequest,
			// SuperDebtorPageChartsStoreActions.ExportAgeingBalanceRequest,
			// SuperDebtorPageChartsStoreActions.ExportEstimatedCashingRequest,

			// HomeActionsStoreActions.ExportAgeingBalanceRequest,
			// HomeActionsStoreActions.ExportHomeEstimatedCashingKpisRequest,

			DunningActionDetailStoreActions.AddAttachmentsRequest,
			DunningActionDetailStoreActions.DeleteAttachmentRequest,
			DunningActionDetailStoreActions.UpdateDunningActionRequest,
			DunningActionDetailStoreActions.CreateDunningActionRequest,
			DunningActionDetailStoreActions.DeleteDunningActionRequest,
			DunningActionDetailStoreActions.CompleteDunningActionRequest,

			// AccountingDocumentActions.OpenDunningLevelForm,
			// AccountingDocumentActions.UploadAccountingDocumentPDFRequest,
			// AccountingDocumentActions.DeleteAccountingDocumentPDFRequest,
		),
		withLatestFrom(this.store.select(featureSelectors.selectIsInitFinished)),
		filter(([_, isInitFinished]) => !!isInitFinished),
		map(([action]) => action),
		map(action => featureActions.InfoLog({ message: action.type, data: action }))
	));

	toastsResetEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.ToastReset),
		tap(_ => {
			this.notificationService.reset();
		})
	), {dispatch: false});

	toastEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.ToastError, featureActions.ToastWarning, featureActions.ToastSuccess),
		tap(action => {
			this.notificationService.notify({
				type: action.level,
				message: action.message,
				messageParams: action.messageParams,
				options: action.options
			});
		})
	), {dispatch: false});

	errorLogEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.ErrorLog),
		tap(action => {
			console.error(action.error);
			if (errorShouldBeSentToAI(action.error)) {
				// FIXME: TelemetryService cannot be injected directly yet
				this.injector.get(TelemetryService).error(action.error);
			}
		})
	), {dispatch: false});

	infoLogEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.InfoLog),
		tap(action => {
			 
			console.info(action.message);
			// FIXME: TelemetryService cannot be injected directly yet
			this.injector.get(TelemetryService).event(action.message);
		})
	), {dispatch: false});

	startInitializerEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.StartInitializer),
		withLatestFrom(
			this.store.select(featureSelectors.selectCurrentUser),
			this.store.select(featureSelectors.selectConfig)),
		switchMap(([_, user, config]) => user?.id ? [
			featureActions.UpdateAppConfigurationSuccess({ config })
		] : [
			// plug here config-independent actions
			featureActions.UpdateAppConfigurationRequest(),
		])
	));

	loadValidatorsEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadValidatorsRequest),
		switchMap(_ => this.validationService.loadValidationRuleset()),
		map(_ => featureActions.LoadValidatorsSuccess()),
		catchError((bootstrapError: Error) => of(featureActions.CriticalFailure({bootstrapError})))
	));

	onCriticalFailure$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.CriticalFailure),
		withLatestFrom(this.store.select(featureSelectors.selectConfig)),
		tap(([action, config]) => {
			if (config.consoleUrl && action.goToConsole) {
				console.error(`Encountered a critical error, you will be redirected.`, action.error);
				setTimeout(_ => location.assign(config.consoleUrl), AppConstants.CRITICAL_ERROR_RELOAD_TIMEOUT);
			} else if (!action.bootstrapError) {
				console.error(`Encountered a critical error, authentication issue page will be displayed.`, action.error);
				const issueFullPath = RoutesDefinitions.getAuthenticationIssueFullPath();
				this.router.navigate([issueFullPath]);
			} else {
				// there is not much we can do
				// just reload and hope for the best
				setTimeout(_ => location.reload(), 10000);
			}

		})
	), {dispatch: false});

	updateConfigEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateAppConfigurationRequest),
		switchMap(_ => from(this.appConfig.ensureInit())),
		withLatestFrom(this.languageService.language$),
		tap(([config, currentLang]) => {
			if (!currentLang && config.defaultLang) {
				this.languageService.setCurrentLanguage(config.defaultLang);
			}
		}),
		switchMap(_ => [
			featureActions.UpdateAppConfigurationSuccess({config: this.appConfig}), // Setups initial AppBaseConfiguration values
			// plug here config-dependant pre-user actions
			featureActions.LoadFactorConfigurationRequest()
		])
	));

	defaultThemeEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateAppConfigurationSuccess),
		map(action => {
			if (action.config.defaultTheme) this.themeService.useTheme(action.config.defaultTheme)
			return featureActions.Noop()
		})
	));

	loadTheme$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.Login, featureActions.UserDataSuccess),
		withLatestFrom(this.store.select(featureSelectors.selectCurrentUser)),
		map(([_, userData]) => userData?.userPrefs?.themeId || 'default'),
		distinctUntilChanged(), // do not emit every time userPrefs change!
		tap(themeId => this.themeService.useTheme(themeId)),
		map(_ => featureActions.Noop())
	));

	// Get stored user data and initialize user PageSizes
	loadPageSizesRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.Login, featureActions.UserDataSuccess),
		withLatestFrom(this.store.select(featureSelectors.selectCurrentUser)),
		filter(([_, userData]) => !!userData?.userPrefs?.pageSizes),
		map(([_, userData]) => UserPrefsStoreActions.LoadPreferedPageSizesSuccess({ entity: userData?.userPrefs?.pageSizes || {} }))
	));

	// Get stored user data and initialize user filters
	loadUserFiltersRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.Login, featureActions.UserDataSuccess),
		withLatestFrom(this.store.select(featureSelectors.selectCurrentUser)),
		filter(([, userData]) => !!userData),
		map(([, userData]) => UserPrefsStoreActions.LoadUserFiltersSuccess({ entity: userData.userFilters }))
	));

	checkInitializersEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateAppConfigurationSuccess,
			featureActions.LoadValidatorsSuccess,
			featureActions.LoadTenantIdentitySuccess,
			featureActions.LoadFactorConfigurationSuccess),
		withLatestFrom(
			this.store.select(featureSelectors.selectState),
			this.ffs.configChanged$,
		),
		tap(([action]) => {
			 
			if (environment.dev) console.debug('[init] check after', action.type);
		}),
		switchMap(([_, state, flags]) => this.translateService.get('App.Name', {failSilently: true}).pipe(map(t => [state, flags, t]))),
		switchMap(([state, _flags, translateServiceLoaded]) => {
			const configIsPresent = !!state.config;
			// const flagsArePresent = !!Object.keys(flags).length;
			const factorConfigIsLoaded = !!state.factorConfig.entity?.countries;
			const tenantIsLoaded = !state.config.consoleUrl || !!state.tenant.entity?.id;

			const actions = [];

			// app is bootstrapped, factor and tenant are loaded, translateService is loaded
			if (
				   configIsPresent 
				// && factorConfigIsLoaded 
				&& tenantIsLoaded 
				&& translateServiceLoaded 
				// && flagsArePresent
			) {
				actions.push(featureActions.AppReady());
				// 				actions.push(featureActions.InfoLog({ message: `
				// ===================================================================================================
				// app is ready in ${(new Date().getMilliseconds() - window['__aston_bootstrap_time'])/1000}ms
				// ===================================================================================================
				// ` }));
			} else if (configIsPresent && factorConfigIsLoaded) {
				actions.push(featureActions.FinishInitializer());
			}

			return actions;
		})
	));

	loadFactorConfiguration$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadFactorConfigurationRequest),
		switchMap(_ => this.factorConfigurationService.getFactorConfig().pipe(
			map(entity => featureActions.LoadFactorConfigurationSuccess({entity})),
			catchError(error => of(featureActions.LoadFactorConfigurationFailure({error})))
		))
	));

	loadFeatureFlagsOnConfig$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateAppConfigurationSuccess),
		switchMap(_ => this.ffs.loadConfig({ ...DefaultFeatureFlags, ...this.appConfig.featureFlags })),
		map(_ => featureActions.Noop()),
	));

	openConfirmationModal$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.OpenConfirmationModal),
		tap(action => {
			const modalRef = this.modalService.open(ConfirmModalComponent, {...AppConstants.DEFAULT_MODAL_OPTIONS, ...action.options});
			modalRef.componentInstance.contentConfig = action.content;
			from(modalRef.componentInstance.answer).pipe(
				map((reason: IConfirmModalResult) => {
					this.store.dispatch(featureActions.CloseConfirmationModal({
						actionType: reason.action, correlationParams: action.correlationParams
					}));
					if (reason.action === ActionType.CANCEL && action.cancelAction) {
						[].concat(action.cancelAction).forEach(a => this.store.dispatch(a));
					}
					if (reason.action === ActionType.VALIDATE && action.confirmationAction) {
						if (action.confirmationAction instanceof Function) {
							action.confirmationAction(reason).forEach(a => this.store.dispatch(a));
						} else {
							[].concat(action.confirmationAction).forEach(a => this.store.dispatch(a));
						}
					}
				}),
				first(),
				catchError(reason => {
					this.store.dispatch(featureActions.CloseConfirmationModal({
						actionType: reason, correlationParams: action.correlationParams
					}));
					if (reason === ActionType.CANCEL && action.cancelAction) {
						[].concat(action.cancelAction).forEach(a => this.store.dispatch(a));
					}
					return EMPTY;
				}),
			).subscribe();
		})
	), {dispatch: false});

	onNavigate$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.Navigate),
		tap(action => {
			this.router.navigate(action.to, action.extras);
		})
	), {dispatch: false});

	onNavigateUrl$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.NavigateByUrl),
		tap(action => {
			this.router.navigateByUrl(action.to);
		})
	), {dispatch: false});

	onNotFound$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.NotFound),
		tap(context => this.router.navigateByUrl('/not-found', {skipLocationChange: true, state: {context}}))
	), {dispatch: false});

	onNavigateBack$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.NavigateBack),
		map(_ => {
			if (this.browserNav.hasPrevious) {
				window.history.back();
				return featureActions.Noop();
			} else {
				return featureActions.Navigate({
					to: [RoutesDefinitions.getBasePath()]
				});
			}
		})
	));

	loadUserOnFinishInit$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.Login),
		withLatestFrom(this.store.select(featureSelectors.selectConfig)),
		tap(_ => {
			if (window.parent) {
				try {
					// Dispatch an event to the parent window (aka the Console)
					window.parent.dispatchEvent(new CustomEvent('aston-auth-done'));
				} catch (e) {
					// Task 33713: [MO] Front / E2E / Play nice with Cypress
					console.warn(`[init] couldn't dispatch auth event higher up`, e)
				}
			}
		}),
		switchMap(([action]) => [
			// Put here post-user actions
			featureActions.Execute(() => this.languageService.setCurrentLanguage(action.userData.language)),
			featureActions.Execute(() => this.referential.loadReferential()),
			featureActions.LoadTenantIdentityRequest(),
			featureActions.LoadValidatorsRequest(),
		])
	));

	afterAppReady$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.Login),
		switchMap(_ => [
			// featureActions.Execute(() => this.templatesStore.loadClips()),
			// featureActions.Execute(() => this.templatesStore.loadAllTemplates()),
			featureActions.Execute(() => this.importDetailStore.loadImportConfig()),
			featureActions.Execute(() => this.extraStore.loadAllExtradataConfigurations()),
			featureActions.Execute(() => runInInjectionContext(this.injector, () => this.dashboardRoutes.preload())),
			featureActions.Execute(() => this.ws.forceReconnect()),
		])
	));

	loadTenantSuccess$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadTenantIdentityRequest),
		switchMap(_ => this.tenantService.getTenantIdentity().pipe(
			map(entity => featureActions.LoadTenantIdentitySuccess({entity})),
			catchError(error => of(
				featureActions.LoadTenantIdentityFailure({error}),
				featureActions.CriticalFailure({bootstrapError: error}),
			))
		))
	));

	startUserSession$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.StartUserSession),
		switchMap(action => this.userService.loadStoredUserSession().pipe(
			switchMap(storedSession => {
				const actions = [];

				if (storedSession && storedSession.username && storedSession.username === action.userName && !!storedSession.lastRoute) {
					// Same user with a saved route
					actions.push(featureActions.UpdateUserSessionRequest({source: 'just logged in'}));
					// actions.push(featureActions.NavigateToLastSavedRoute({route: storedSession.lastRoute}));
				} else {
					// Different user, no need to redirect
					actions.push(featureActions.UpdateUserSessionRequest({source: 'new user', userSession: {username: action.userName, lastRoute: null}}));
				}

				return actions;
			}),
			tap(() => {
				this.inactivityService.init();
			})
		))
	));

	inactivityWarning$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateInactivityTimer),
		auditTime(200),
		filter(_ => !this.isInactivityWarningModalActive),
		switchMap(_ => combineLatest([
			this.inactivityService.inGracefulPeriod$,
			this.store.select(featureSelectors.selectState),
		]).pipe(
			filter(([showInactivityWarning]) => showInactivityWarning && !this.isInactivityWarningModalActive),
			tap(_ => {
				this.modalService.dismissAll();
				this.offcanvasService.dismiss();
			}),
			delay(100),
			map(([_, state]) => {
				const modalRef = this.modalService.open(InactivityModalComponent, AppConstants.DEFAULT_MODAL_OPTIONS);
				const modalInstance: InactivityModalComponent = modalRef.componentInstance;
				this.isInactivityWarningModalActive = true;

				modalInstance.actionResult.pipe(
					debounceTime(200),
					map(event => {
						const reason = event.type;

						if (reason === ActionType.UPDATE) {
							this.store.dispatch(featureActions.UpdateUserSessionRequest({source: 'postponed by inactivity modal'}));

						} else if (reason === ActionType.CLOSE) {
							this.router.navigate([RoutesDefinitions.getLoginFullPath()]);
						}

						setTimeout(_ => {
							this.inactivityService.updateInactivityTimer(state);
							this.isInactivityWarningModalActive = false
						}, 100);
					})
				).subscribe();

				return featureActions.Noop();
			})
		))
	));

	// Hide the inactivity modal, once shown, when the timer has been reset from elsewhere (ie: another tab)
	hideInactivityWarning$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateInactivityTimer),
		auditTime(200),
		filter(_ => this.isInactivityWarningModalActive),
		switchMap(_ => combineLatest([
			this.inactivityService.inGracefulPeriod$,
			this.store.select(featureSelectors.selectState),
		]).pipe(
			filter(([showInactivityWarning]) => !showInactivityWarning && this.isInactivityWarningModalActive),
			tap(_ => {
				this.isInactivityWarningModalActive = false
				this.modalService.dismissAll()
			})
		))
	), {dispatch:false});


	updateInactivityTimer$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateInactivityTimer),
		withLatestFrom(this.store.select(featureSelectors.selectState)),
		tap(([_, state]) => this.inactivityService.updateInactivityTimer(state))
	), {dispatch: false});

	stopUserSession$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateInactivityTimer),
		withLatestFrom(this.inactivityService.isInactive$),
		filter(([_, isInactive]) => isInactive),
		tap(_ => this.authenticationService.logout({ keepNavigation: true }))
	), {dispatch: false});

	updateUserSessionRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateUserSessionRequest),
		withLatestFrom(this.store.select(featureSelectors.selectCurrentUser)),
		filter(([_, user]) => !!user),
		switchMap(([action]) => this.userService.loadStoredUserSession().pipe(
			switchMap(storedSession => {
				const userSession: IUserSession = {
					...storedSession,
					...action.userSession,
					lastActivityDate: new Date(),
					inactivityTime: 0
				};
				 
				if (window['__aston_debug_inactivity']) console.debug('[inactivity] update session because:', action.source)
				this.userService.saveUserSession(userSession);
				return of(featureActions.UpdateUserSessionSuccess({userSession}));
			})
		))
	));

	navigateToLastSavedRoute$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.NavigateToLastSavedRoute),
		debounceTime(500),
		filter(action => !window.location.pathname.endsWith(action.route)),
		map(action => featureActions.OpenConfirmationModal(
			{textsKey: 'UserSession.PostAuthenticationNavigation.'},
			featureActions.Navigate({to: [action.route]}),
			featureActions.UpdateUserSessionRequest({source: 'inactive', userSession: {lastRoute: null}})
		))
	));

	loadUserOnFinish$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.FinishInitializer),
		switchMap(_ => this.userService.loadUserData().pipe(
			map(userData => featureActions.UserDataSuccess({userData}))
		)),
		// plug here non store initializers
	));

	loadScript$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadScriptRequest),
		switchMap(action => this.scriptLoaderService.load(action.script).pipe(
			map(_ => featureActions.LoadScriptSuccess({script: action.script})),
			catchError(error => of(featureActions.ErrorLog(error)))
		))
	));

	downloadDocument$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.DownloadDocument),
		switchMap(action => {
			if (action.document.id) {
				this.store.dispatch(featureActions.ToastWarning('Actions.PreparingFile'));
				return this.documentFileService.exportDocument(action.document).pipe(
					tap(response => saveAs(response.blob, response.fileName)),
					delay(800),
					tap(_ => this.store.dispatch(featureActions.ToastReset())),
					catchError(error => of(featureActions.ErrorLog(error)))
				);
			} else if (action.document.url && action.document.url['changingThisBreaksApplicationSecurity']) {
				this.store.dispatch(featureActions.ToastWarning('Actions.PreparingFile'));
				return of(null).pipe(
					tap(_ => saveAs(action.document.url['changingThisBreaksApplicationSecurity'], action.document.name)),
					delay(800),
					tap(_ => this.store.dispatch(featureActions.ToastReset())),
					catchError(error => of(featureActions.ErrorLog(error)))
				)
			} else {
				return of(featureActions.ErrorLog(`Cannot open document ${JSON.stringify(document)}`));
			}
		})
	), {dispatch: false});

	openFileOfDocument$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.OpenFileOfDocument),
		switchMap(action => this.documentFileService.getFilesOfDocument(action.document.id).pipe(
			filter(documents => documents.length > 0),
			map(documents => featureActions.OpenDocument({ document: documents[0] })),
		)
		)));

	openDocument$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.OpenDocument),
		switchMap(action => {
			// local file, or URL already fetched
			if (action.document.url && !!action.document.url['changingThisBreaksApplicationSecurity']) {
				return of(featureActions.OpenUrl({
					url: action.document.url['changingThisBreaksApplicationSecurity'],
					fileName: action.document.name
				}));
				// a document with an an id
			} else if (action.document.id) {
				return this.documentFileService.getLocalFileURL(action.document).pipe(
					map(document => featureActions.OpenUrl({
						url: document.url['changingThisBreaksApplicationSecurity'],
						fileName: action.document.name
					})),
					catchError(error => of(featureActions.ErrorLog(error)))
				);
				// unhandled
			} else {
				return of(featureActions.ErrorLog(`Cannot open document ${JSON.stringify(document)}`));
			}
		})
	));

	onPreviewDocumentFile$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.PreviewDocumentFile),
		tap(action => {
			if (!action.overlap) this.offcanvasService.dismiss();
			const ref = this.offcanvasService.open(DocumentFilesViewerComponent, AppConstants.DEFAULT_OFFCANVAS_OPTIONS);
			ref.componentInstance.files = [action.document];
			ref.componentInstance.title = action.document.name;
		})
	), { dispatch: false });

	onPreviewDocument$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.PreviewDocument),
		tap(action => {
			const ref = this.offcanvasService.open(DocumentFilesViewerComponent, AppConstants.DEFAULT_OFFCANVAS_OPTIONS);
			ref.componentInstance.item = { id: action.document.id };
			ref.componentInstance.title = action.document.name;
		})
	), { dispatch: false });

	// openURL$ = createEffect(() => this.actions$.pipe(
	// 	ofType(featureActions.OpenUrl),
	// 	tap(action => {
	// 		window.open(action.url, '_blank');
	// 	}),
	// 	catchError(error => {
	// 		// the browser probably blocked our window to open
	// 		error.message = 'Cannot open window. ' + error.message;
	// 		return of(featureActions.ErrorLog(error));
	// 	})
	// ), {dispatch: false});

	openLink$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.OpenUrl),
		tap(action => {
			const a = document.createElement('a');
			let url = action.url;
			if (url.startsWith('/')) {
				// relative urls should use current location+tenantId
				url = (new TenantUrl(url)).toString();
			}
			a.setAttribute('href', url);
			a.setAttribute('target', '_blank');
			if (action.download) {
				a.setAttribute('download', action.fileName);
			}
			a.click();
		}),
		catchError(error => {
			// the browser probably blocked our window to open
			error.message = 'Cannot open window. ' + error.message;
			return of(featureActions.ErrorLog(error));
		})
	), {dispatch: false});

	printDocument$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.PrintDocument),
		mergeMap(action => {
			// local file, or URL already fetched
			if (action.document.url && !!action.document.url['changingThisBreaksApplicationSecurity']) {
				return of(featureActions.PrintUrl(action.document.url['changingThisBreaksApplicationSecurity']));
				// a document with an an id
			} else if (action.document.id) {
				return this.documentFileService.getLocalFileURL(action.document).pipe(
					map(document => featureActions.PrintUrl({url: document.url['changingThisBreaksApplicationSecurity']})),
					catchError(error => of(featureActions.ErrorLog(error)))
				);
				// unhandled
			} else {
				return of(featureActions.ErrorLog(`Cannot print document ${JSON.stringify(document)}`));
			}
		})
	));

	printURL$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.PrintUrl),
		tap(action => {
			window.open(action.url, '_blank').print();
		}),
		catchError(error => {
			// the browser probably blocked our window to open
			error.message = 'Cannot open print window. ' + error.message;
			return of(featureActions.ErrorLog(error));
		})
	), {dispatch: false});

	LogoutEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.Logout),
		tap(_ => this.authenticationService.logout({ keepNavigation: false }))
	), {dispatch: false});

	NavigateToConsoleEffect = createEffect(() => this.actions$.pipe(
		ofType(featureActions.NavigateToConsole),
		withLatestFrom(this.store.select(featureSelectors.selectConfig)),
		map(([action, config]) => {
			window.location.href = `${config.consoleUrl}${action.path}`;
		})
	), {dispatch: false});

	NavigateToGateEffect = createEffect(() => this.actions$.pipe(
		ofType(featureActions.NavigateToGate),
		withLatestFrom(this.store.select(featureSelectors.selectConfig)),
		map(([action, config]) => {
			const language = this.languageService.getCurrentLanguage()
			window.open(`${config.authenticationServerUrl}${action.path}&culture=${language}`, '_blank');
		})
	), {dispatch: false});

	NavigateToHelpPageEffect = createEffect(() => this.actions$.pipe(
		ofType(featureActions.NavigateToHelpPage),
		withLatestFrom(this.store.select(featureSelectors.selectConfig)),
		map(([_, config]) => {
			window.open(`${config.helpUrl}`, '_blank');
		})
	), {dispatch: false});

	RefreshUserEffect = createEffect(() => this.ws.ofType(
		WsEventType.BadgeAchievement,// some achievement unlocked
		WsEventType.UserSignOut      // some user logged out. if it's us, we'll redirect
	).pipe(
		debounceTime(500),
		switchMap(_ => this.userService.refreshUserInfo()),
		map(userData => featureActions.UserDataSuccess({userData}))
	));

	// Set language when user details change
	updateLanguage$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UserDataSuccess),
		filter(action => !!action.userData?.language),
		map(action => featureActions.Execute(() => this.languageService.setCurrentLanguage(action.userData.language)))
	));

	executeFnEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.Execute),
		tap(action => action.fns.forEach(fn => fn())),
		map(_ => featureActions.Noop())
	));
}
